import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {useSlateSelection} from "slate-react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {border, color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useGuideState} from "@khan/guide-state";

import AskKhanmigoTextInput from "./ask-khanmigo-text-input.tsx";
import type {Suggestion} from "./suggestions.ts";

/**
 * This is the component that appears within the popover when you have some
 * text selected in the coeditor. It renders a list of "suggestions": requests
 * or edits that you may want to ask of the LLM. The actual suggestion content
 * is passed in as a prop and will vary depending on what you have selected,
 * but this component dictates how those suggestions are displayed.
 */
export default function AskKhanmigoMenu(props: {
    yOffset: number;
    suggestions: Suggestion[];
    handleSuggestionClick: (suggestion: Suggestion) => void;
}) {
    const {yOffset, suggestions, handleSuggestionClick} = props;

    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");
    const selection = useSlateSelection();

    // If a suggestion is clicked that requires additional details (for
    // example, "Make changes to this", which requires the user to specify what
    // changes they want), we'll set this next bit of state to the suggestion
    // that we're editing:
    const [editingSuggestion, setEditingSuggestion] =
        useState<Suggestion | null>(null);

    useEffect(() => {
        setEditingSuggestion(null);
    }, [selection]);

    const handleSuggestionAndClose = useCallback(
        (suggestion: Suggestion) => {
            setEditingSuggestion(null);
            handleSuggestionClick(suggestion);
        },
        [handleSuggestionClick],
    );

    const handleItemClick = useCallback(
        (suggestion: Suggestion) => {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideCoeditorToolPopupActionSelected",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
                selectedAction: suggestion.action,
            });

            if (suggestion.needsAdditionalInput) {
                // Some suggestions need the user to provide an extra bit
                // of detail! Switch to a text input so the user can add it:
                setEditingSuggestion(suggestion);
            } else {
                // Otherwise, send the message indicated by this suggestion
                // and close the dropdown:
                handleSuggestionAndClose(suggestion);
            }
        },
        [handleSuggestionAndClose, navigation.id, threadId],
    );

    if (editingSuggestion) {
        return (
            <View style={[styles.menu, styles.editingMenu, {top: yOffset}]}>
                <AskKhanmigoTextInput
                    suggestion={editingSuggestion}
                    updateSuggestion={setEditingSuggestion}
                    handleSuggestionClick={handleSuggestionAndClose}
                />
            </View>
        );
    }

    return (
        <View style={[styles.menu, {top: yOffset}]}>
            {suggestions.map((s, i) => (
                <Tooltip content={s.text()} placement="right" key={i}>
                    <IconButton
                        testId={s.action}
                        size="small"
                        kind="tertiary"
                        style={styles.suggestionIconButton}
                        icon={s.icon}
                        onClick={() => handleItemClick(s)}
                        aria-label={s.text()}
                    />
                </Tooltip>
            ))}
        </View>
    );
}

const styles = StyleSheet.create({
    menu: {
        position: "absolute",
        zIndex: 10,
        top: 0,
        transform: "translateY(-50%)",
        insetInlineEnd: -spacing.small_12,
        width: spacing.xxLarge_48,
        background: color.fadedPurple8,
        border: `1px solid ${color.offBlack16}`,
        padding: spacing.xxSmall_6,
        boxShadow: `0 8px 8px 0 rgba(0,0,0,0.08),
            0 4px 4px rgba(0,0,0,0.08) !important`,
        borderRadius: border.radius.radius_040,
        gap: spacing.xxxSmall_4,
        alignItems: "center",
    },
    editingMenu: {
        background: color.white,
        width: "unset",
    },
    suggestionIconButton: {
        color: color.eggplant,
        [":hover:not([aria-disabled=true])" as any]: {
            background: color.fadedBlue16,
            border: `1px solid ${color.activeBlue}`,
        },
    },
});
